import type { CatchError } from './functional/error.no-deps'
import { raceWithTimeout } from './race.no-deps'

export interface BusTask<T extends string> {
    type: T
    label: string
    timeout?: number
    resolves: Array<() => void>
    rejects: Array<(error: CatchError) => void>
}

export interface BusHandler<T extends string> {
    type: T
    action: Action
}

export type Action = () => Promise<void>

export interface BusOptions {
    singleHandlerPerType: boolean
    singleTaskOfTypeInQueue: boolean
    defaultTimeout: number
    log: (message: string) => void
    reportError: (error: CatchError) => void
    requestIdleCallback?: (callback: () => void) => void
}

/**
 * Service bus for background tasks. Tasks are processed one at a time unless they timeout.
 */
export class Bus<T extends string> {
    private queue: Array<BusTask<T>> = []
    private handlers: Array<BusHandler<T>> = []
    private isProcessing = false

    constructor(private opts: BusOptions) {}

    public addTask(type: T, label: string, timeout?: number) {
        // Return a promise if you wish to wait for task to be completed
        return new Promise<void>((resolve, reject) => {
            // If there's no handler for this type then reject
            if (!this.hasHandlerForType(type)) {
                reject(new Error(`No registered handler for task type ${type}`))
                return
            }

            const previousTask = this.findTaskOfType(type)

            // If there's already a task of this type
            if (this.opts.singleTaskOfTypeInQueue && previousTask != null) {
                // Resolve promise when previous task is completed
                previousTask.resolves.push(resolve)
                previousTask.rejects.push(reject)

                // Log defferal to previous task
                this.opts.log(`Bus: Task ${type} "${label}" deffered to previous task "${previousTask.label}"`)

                return
            }

            // Otherwise push new task onto queue
            this.queue.push({ type, label, timeout, resolves: [resolve], rejects: [reject] })

            // Log task addition
            this.opts.log(`Bus: Task ${type} "${label}" queued. ${this.queue.length} tasks in queue.`)

            // Start processing if not currently
            return this.startProcessing()
        })
    }

    public registerHandler(type: T, action: Action) {
        // If there's already a handle of this type then throw
        if (this.opts.singleHandlerPerType && this.hasHandlerForType(type)) {
            throw new Error(`Already has handler registered for task type ${type}`)
        }

        this.handlers.push({ type, action })
    }

    public hasTaskOfType(type: T) {
        return this.findTaskOfType(type) != null
    }

    public hasHandlerForType(type: T) {
        return this.findHandlerForType(type) != null
    }

    private findTaskOfType(type: T) {
        return this.queue.find((t) => t.type === type)
    }

    private findHandlerForType(type: T) {
        return this.handlers.find((h) => h.type === type)
    }

    public logQueue() {
        this.opts.log(JSON.stringify(this.queue, null, 2))
    }

    private async startProcessing() {
        // Do not start processing if already processing
        if (this.isProcessing) return
        this.isProcessing = true

        // Loop until the queue is empty
        while (this.queue.length > 0) {
            // Catch all errors again just to be defensive so loop continues to run
            await this.processTask().catch(this.opts.reportError)
        }

        this.isProcessing = false
    }

    private async processTask() {
        // If the system is busy then wait till later
        if (this.opts.requestIdleCallback != null) {
            await new Promise<void>(this.opts.requestIdleCallback)
        }

        // Take the first task from the queue
        const task = this.queue.shift()
        try {
            // Log start
            this.opts.log(`Bus: Task ${task.type} "${task.label}" started. ${this.queue.length} tasks waiting.`)

            // Get handler for task type
            const handler = this.findHandlerForType(task.type)

            // Default timeout if not provided
            const timeout = task.timeout || this.opts.defaultTimeout

            // Run task as a race with timeout
            await raceWithTimeout(timeout, handler.action)

            // Log end
            this.opts.log(`Bus: Task ${task.type} "${task.label}" completed successfully`)

            // Resolve task promises
            task.resolves.forEach((resolve) => resolve())
        } catch (error) {
            // Log failure with task type for context
            this.opts.log(`Bus: Task ${task.type} "${task.label}" failed`)

            // Pass back error object as is with call stack
            this.opts.reportError(error)

            // Reject task promises
            task.rejects.forEach((reject) => reject(error))
        }
    }
}
